import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AppShell } from './components/AppShell.js'
import { RequireAdmin } from './components/RequireAdmin.js'
import { RequireAuth } from './components/RequireAuth.js'
import { AuthProvider, useAuth } from './lib/auth-context.js'
import { loadEphemeralSession } from './lib/ephemeral-session.js'
import { QueryProvider } from './lib/query-client.js'
import { ToastProvider } from './lib/toast.js'
import { AccessLogsPage } from './pages/AccessLogs.js'
import { AccountFrozenPage } from './pages/AccountFrozen.js'
import { AdminDashboardPage } from './pages/AdminDashboard.js'
import { AdminPlanUpgradeRequestsPage } from './pages/AdminPlanUpgradeRequests.js'
import { AdminProjectFilesPage } from './pages/AdminProjectFiles.js'
import { AdminTenantDetailPage } from './pages/AdminTenantDetail.js'
import { AdminTenantsPage } from './pages/AdminTenants.js'
import { AuthCallbackPage } from './pages/AuthCallback.js'
import { LandingPage } from './pages/Landing.js'
import { LoginPage } from './pages/Login.js'
import { NotificationEmailConfirmPage } from './pages/NotificationEmailConfirm.js'
import { OnboardingMigratedPage } from './pages/OnboardingMigrated.js'
import { PlanUpgradePage } from './pages/PlanUpgrade.js'
import { ProjectDetailPage } from './pages/ProjectDetail.js'
import { ProjectsPage } from './pages/Projects.js'
import { SettingsPage } from './pages/Settings.js'
import { SharePage } from './pages/SharePage.js'
import { SignupPage } from './pages/Signup.js'

/**
 * `/` の振り分け:
 *   - Supabase セッションあり → /projects（ログイン済みオーナー）
 *   - 暫定テナントセッションあり → /projects（フック領域）
 *   - どちらも無し → 公開トップ（Landing）
 */
function RootRoute() {
  const { session, loading } = useAuth()
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-sm text-muted-foreground">読み込み中...</p>
      </div>
    )
  }
  if (session) {
    // 管理者 (app_metadata.is_admin) は /admin、通常ユーザーは /projects へ振り分け
    const isAdmin = (session.user.app_metadata as { is_admin?: boolean } | undefined)?.is_admin
    return <Navigate to={isAdmin ? '/admin' : '/projects'} replace />
  }
  if (loadEphemeralSession()) {
    return <Navigate to="/projects" replace />
  }
  return <LandingPage />
}

export function App() {
  return (
    <QueryProvider>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/share/:token" element={<SharePage />} />
              <Route path="/account/frozen" element={<AccountFrozenPage />} />
              <Route
                path="/account/notification-email/confirm"
                element={<NotificationEmailConfirmPage />}
              />
              <Route path="/onboarding/migrated" element={<OnboardingMigratedPage />} />
              <Route
                element={
                  <RequireAuth>
                    <AppShell variant="tenant" />
                  </RequireAuth>
                }
              >
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/projects/:id" element={<ProjectDetailPage />} />
                <Route path="/projects/:id/access-logs" element={<AccessLogsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/upgrade" element={<PlanUpgradePage />} />
              </Route>
              <Route
                element={
                  <RequireAdmin>
                    <AppShell variant="admin" />
                  </RequireAdmin>
                }
              >
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/tenants" element={<AdminTenantsPage />} />
                <Route
                  path="/admin/plan-upgrade-requests"
                  element={<AdminPlanUpgradeRequestsPage />}
                />
                <Route path="/admin/tenants/:id" element={<AdminTenantDetailPage />} />
                <Route
                  path="/admin/tenants/:id/projects/:pid/files"
                  element={<AdminProjectFilesPage />}
                />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </QueryProvider>
  )
}
